import {Action, Module, Mutation} from 'vuex-module-decorators'
import store from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import ILanguageCreateDto = MdbsCore.ILanguageCreateDto;
import ILanguageUpdateDto = MdbsCore.ILanguageUpdateDto;
import LanguageUpdateDto = MdbsCore.LanguageUpdateDto;
import LanguageCreateDto = MdbsCore.LanguageCreateDto;

const languageClient = new MdbsCore.LanguageClient();
// lang identifier, study Id, scope, displayKey
type languagesType = Record<string, Record<string, Record<string, Record<string, string>>>>;
const defaultStudyId = "default";
const defaultScope = "default";

@Module({namespaced: true, name: 'language', store: store})
export default class LanguageModule extends BaseModule {

    languages: languagesType = {};

    get translate() {
        return (displayKey: string, languageIdentifier: string, studyId?: string, scope?: string) => {
            const language = this.languages[languageIdentifier];
            if (!language) return displayKey;
            if (studyId) {
                const studyLang = language[studyId]
                if (studyLang) {
                    if (scope) {
                        const scopeLang = studyLang[scope];
                        if (scopeLang) {
                            const value = scopeLang[displayKey];
                            if (value) {
                                return value
                            }
                        }
                    }
                    const scopeLang = studyLang[defaultScope];
                    if (scopeLang) {
                        const value = scopeLang[displayKey];
                        if (value) {
                            return value
                        }
                    }
                }
            }
            const defaultLang = language[defaultStudyId]
            if (defaultLang) {
                if (scope) {
                    const scopeLang = defaultLang[scope];
                    if (scopeLang) {
                        const value = scopeLang[displayKey];
                        if (value) {
                            return value
                        }
                    }
                }
                const scopeLang = defaultLang[defaultScope];
                if (scopeLang) {
                    const value = scopeLang[displayKey];
                    if (value) {
                        return value
                    }
                }
            }

            // console.warn("Missing DisplayKey: " + displayKey)
            return displayKey
        }
    }

    @Mutation
    setLanguages(languages: languagesType) {
        this.languages = languages;
    }

    @Mutation
    reset() {
        this.languages = {};
    }

    @Action({rawError: true})
    async loadAll() {
        await this.execute(async () => {
                const shortLanguages = await languageClient.getAllLanguages(false);
                const languages: languagesType = {};
                for (let shortLanguage of shortLanguages) {
                    const language = await languageClient.getById(shortLanguage.id)
                    const langIdentifier = language.identifier ?? "";
                    // "||" for the same reason as the scope below: an empty id is "not study-specific".
                    const langStudyId = language.studyDefinitionId || defaultStudyId
                    if (!languages[langIdentifier]) {
                        languages[langIdentifier] = {}
                    }
                    const languageByIdentifier = languages[langIdentifier]
                    if (!languageByIdentifier[langStudyId]) {
                        languageByIdentifier[langStudyId] = {}
                    }
                    const languageByStudyId = languageByIdentifier[langStudyId]

                    // Deleted entries must not shadow the active translation of the same key.
                    for (let entry of (language.entries ?? []).filter(e => !e.isDeleted)) {
                        // "||" on purpose: an empty scope means "no scope" and belongs in the default
                        // bucket. With "??" it would land in a bucket named "" that no lookup ever reads.
                        const langScope = entry.scope || defaultScope
                        if (!languageByStudyId[langScope]) {
                            languageByStudyId[langScope] = {}
                        }
                        const languageByScope = languageByStudyId[langScope];
                        languageByScope[entry.displayKey ?? ""] = entry.displayValue ?? ""
                    }
                }
                this.setLanguages(languages)
            }
        ).catch(() => this.reset());
    }

    @Action({rawError: true})
    async create(language: ILanguageCreateDto) {
        await this.execute(async () => {
            await languageClient.create(new LanguageCreateDto(language))
        }).catch();
        await this.loadAll();
    }

    @Action({rawError: true})
    async update(language: ILanguageUpdateDto) {
        await this.execute(async () => {
            await languageClient.update(language.id, new LanguageUpdateDto(language));
        }).catch();
        await this.loadAll();
    }

    @Action({rawError: true})
    async delete(id: string) {
        await this.execute(async () => {
            await languageClient.delete(id);
            this.reset()
        }).catch();
        await this.loadAll();
    }
}
